/**
 * Dropdown
 * Wrapper for bootstrap Dropdown feature
 * @package feature/dropdown
 */
export class Dropable {
  constructor(element) {
    element.Dropable = this;

    // References
    this._refs = {
      el: element,
      opener: element.querySelector('a, button'),
      menu: element.querySelector('.dropdown-menu, .ui-dropdown-menu'),
      items: Array.from(
        element.querySelectorAll('.dropdown-menu a, .ui-dropdown-menu a, .ui-dropdown-menu button'),
      ),
    };

    this._isPortal = element.hasAttribute('data-portal');

    this._onClickOutside = this._onClickOutside.bind(this);

    if (this._isPortal && this._refs.menu) {
      document.body.appendChild(this._refs.menu);
    }

    // Events
    if (this._refs.opener != null) {
      this._refs.opener.addEventListener('click', this._onLinkClick.bind(this));
      this._refs.opener.addEventListener('keydown', this._onKeyDown.bind(this));
    }

    this._refs.items.forEach((item) => {
      item.addEventListener('click', () => this._close());
      item.addEventListener('keydown', this._onItemKeyDown.bind(this));
    });
  }

  /**
   * _onClickOutside
   * Close dropdown when clicking outside
   * @param {Event} e - Event interface
   */
  _onClickOutside(e) {
    if (!e || !e.target) return;

    if (!this._refs.el.contains(e.target) && !this._refs.menu.contains(e.target)) {
      this._close();
    }
  }

  /**
   * onLinkClick
   * Show a dropdown if user has clicked on trigger (link)
   * @param {Event} e - Event interface
   */
  _onLinkClick() {
    if (this._refs.menu.classList.contains('is-active')) {
      this._close();
    } else {
      this._open();
    }
  }

  /**
   * _getVisibleItems
   * Returns only items whose parent <li> is not hidden (e.g. via is-hidden class).
   * Called on every interaction so dynamically hidden items are always excluded.
   */
  _getVisibleItems() {
    return this._refs.items.filter((item) => {
      const li = item.closest('.dropdown-menu__item, .ui-dropdown-menu__item');
      return !li || !li.classList.contains('is-hidden');
    });
  }

  /**
   * _onKeyDown
   * Keyboard handling on trigger
   */
  _onKeyDown(e) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._open();
      const visible = this._getVisibleItems();
      if (visible.length) visible[0].focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this._close();
    } else if (e.key === 'Tab') {
      this._close();
    }
  }

  /**
   * _onItemKeyDown
   * Keyboard handling on menu items
   */
  _onItemKeyDown(e) {
    const visible = this._getVisibleItems();
    const index = visible.indexOf(e.currentTarget);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = index + 1 < visible.length ? index + 1 : 0;
      visible[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = index - 1 >= 0 ? index - 1 : visible.length - 1;
      visible[prev].focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this._close();
      this._refs.opener.focus();
    } else if (e.key === 'Tab') {
      this._close();
    }
  }

  _open() {
    this._refs.menu.classList.add('is-active');
    this._refs.opener.setAttribute('aria-expanded', 'true');
    this._adjustPosition();
    document.addEventListener('click', this._onClickOutside);
  }

  _close() {
    this._refs.menu.classList.remove('is-active');
    this._refs.opener.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._onClickOutside);
  }

  /**
   * close
   * Public API to close the dropdown from outside the component.
   */
  close() {
    this._close();
  }

  /**
   * _adjustPosition
   * Check if dropdown fits in viewport, adjust vertical and horizontal position if needed
   */
  _adjustPosition() {
    const menu = this._refs.menu;
    const menuRect = menu.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;

    if (!this._isPortal) {
      menu.style.top = '';
      menu.style.bottom = '';
      menu.style.left = '';
      menu.style.right = '';

      if (menuRect.bottom > vh && menuRect.top - menuRect.height > 0) {
        menu.style.top = 'auto';
        menu.style.bottom = 'calc(100% + 8px)';
      }

      if (menuRect.left < 0 && menuRect.left + menuRect.width <= vw) {
        menu.style.right = 'auto';
        menu.style.left = '0';
      }
      return;
    }

    const openerRect = this._refs.opener.getBoundingClientRect();

    let topPos = openerRect.bottom + window.scrollY + 8;
    let leftPos = openerRect.right + window.scrollX - menuRect.width;

    if (openerRect.bottom + menuRect.height > vh) {
      if (openerRect.top - menuRect.height > 0) {
        topPos = openerRect.top + window.scrollY - menuRect.height - 8;
      }
    }

    if (leftPos < window.scrollX) {
      leftPos = openerRect.left + window.scrollX;
    }

    menu.style.top = `${topPos}px`;
    menu.style.left = `${leftPos}px`;
    menu.style.bottom = 'auto';
    menu.style.right = 'auto';
  }
}
